import React, { useState } from "react";
import Editor from "@monaco-editor/react";
import DarkThemeQuestion from "./darkThemeQuestion";
import MarkdownWithLaTeX from "./markDownWithLatex";
import Explanation from "./explanation";
import ASingleTestLambdaGrading from "./aSingleTestLambdaGrading";
import FullScreenToggler from "./fullScreenToggler";
import RunNewTests from "./runNewTests";
import FlagUpdate from "./flagUpdate";
import QuestionLabel from "./questionLabel";
import handleEditor from "./handleEditor";

const LambdaResponseView = ({ blockPaste, saveLogs, updateLogs, pollMode, saveAnswersCritical, updateQuestions, lastStartTime, flags, updateFlags, contentsNeedToBeSaved, updateContentsNeedToBeSaved, contents, questionsToSave, status, attempt, allowedAttempts, endedExam, results, testsMap, explanation, useExplanation, useSyntaxHighlighting, updateQuestionsToSave, useSyntax, useAutocompletion, questionNumber, question, language, isDarkTheme, updateIsDarkTheme, points, grading, uuid, studentAnswer, pointsEarned }) => {    
    const [fullscreen, setFullscreen] = useState(false);

    const handleChange = (value) => {
        if (!grading) {
            updateQuestionsToSave((prev) => ({
                ...prev,
                [uuid]: {
                    answer: value
                }
            }));
    
            updateQuestions((prev) => {
                return prev.map((question) => {
                    if (question.uuid !== uuid) return question;
    
                    return {
                        ...question,
                        studentAnswer: value
                    }
                })
            });
        }        
    }

    const determineIfWeHaveATestToShow = () => {
        const mapsLength = testsMap && Object.keys(testsMap).length > 0;

        if (results && typeof results === "object" && Object.keys(results).length > 0 && mapsLength) {

            for (const key of Object.keys(results)) {
                if (key in testsMap) {
                    return true;
                }
            }
        } else if (results && typeof results === "string" && mapsLength) {
            return true;
        }

        return false;
    }

    function updateFullscreen(fullscreen) {
        setFullscreen(fullscreen);
    }

    return (
        <>
            <div className="true-false-question-view" id={uuid}>
                <div className="question-number">
                    <div className="grid-number-container">
                        <QuestionLabel questionNumber={questionNumber} prefix={"Q"} pollMode={pollMode}/>
                        {
                            !pollMode && (
                                <FlagUpdate flags={flags} updateFlags={updateFlags} uuid={uuid} grading={grading} number={questionNumber}/>
                            )
                        }
                    </div>
                    <div className="pts">
                        { (grading ? Number(pointsEarned).toFixed(2) + " / " : "") + Number(points).toFixed(2) } pts
                    </div>
                </div>
                <div className="true-false-top">
                    <MarkdownWithLaTeX content={question} isDarkTheme={isDarkTheme}/>
                </div>
                <div className="code-response-area-wrapper">
                    <div className={`code-response-area ${fullscreen ? (grading ? "fullscreen-editor-side-bar" : "fullscreen-editor") : ""}`} style={{ backgroundColor: isDarkTheme ? "var(--vs-background)" : "white", color: isDarkTheme ? "white" : "var(--almost-black)"}}>
                        <div className="code-editor-header" style={{ justifyContent: "space-between" }}>
                            <div className="location">
                                Lambda ({language})
                            </div>
                            <div className="code-editor-header" style={{ width: "fit-content" }}>
                                <FullScreenToggler updateFullscreen={updateFullscreen} fullscreen={fullscreen}/>
                                <DarkThemeQuestion isDarkTheme={isDarkTheme} updateIsDarkTheme={updateIsDarkTheme}/>
                            </div>
                        </div>
                        <Editor
                            language={grading ? language : (useSyntaxHighlighting && useSyntax ? language : "plaintext")}
                            value={studentAnswer}
                            theme={ isDarkTheme ? "vs-dark" : "vs"}
                            onChange={(value) => handleChange(value)}
                            options={{
                                fontFamily: "Consolas, 'Courier New', monospace", // Default font family
                                fontSize: 14, // Default font size (14px)
                                tabSize: 8,         // Set tab length to 4 spaces
                                fontWeight: "500", // Default font weight
                                suggestOnTriggerCharacters: useAutocompletion,
                                quickSuggestions: useAutocompletion,
                                readOnly: grading,
                                contextmenu: false, // Disable right-click context menu
                                parameterHints: useAutocompletion, // Disable parameter hints for function calls
                                autoClosingBrackets: true, // Disable automatic closing brackets                         
                            }}
                            onMount={(editor, monaco) => {
                                if (blockPaste) {
                                    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV, () => {
                                        if (localStorage.getItem('blockPaste') === 'true') {
                                            alert("Your instructors have disabled the ability to paste.");
                                            localStorage.setItem('blockPaste', 'false');
                                        }                                        
                                    });
                                }

                                handleEditor(editor, updateLogs);
                            }}
                        />
                        <div className="code-editor-header" />
                    </div>
                </div>
                {
                    grading && endedExam && determineIfWeHaveATestToShow() && (  
                        <div className="true-false-top lambda-results-container">
                            {

                                results && typeof results === "object" ?
                                
                                Object.keys(results).map((id) => {                            

                                    if (id in testsMap) {
                                        const test_result = results[id];
                                        const original_test = testsMap[id];

                                        return <ASingleTestLambdaGrading passPoints={original_test.passPoints} greyedOut={status === "RUNNING"} key={id} testName={original_test.testName} pointsEarned={original_test[test_result.passed ? "passPoints" : "failPoints"]} output={test_result.output} passed={test_result.passed}/>
                                    }
                                })

                                :

                                <div style={{ color: status === "RUNNING" ? "var(--gray-six)" : "" }}>
                                    {results}
                                </div>
                            }                                
                        </div>                            
                    )
                }
                {
                    !grading && language && language !== "plaintext" && allowedAttempts !== null && allowedAttempts !== undefined && allowedAttempts !== 0 && testsMap && Object.keys(testsMap).length > 0 && (
                        <div className="select-all-that-apply-array" style={{ marginTop: "10px" }}>
                            <RunNewTests saveLogs={saveLogs} pollMode={pollMode} saveAnswersCritical={saveAnswersCritical} updateQuestions={updateQuestions} lastStartTime={lastStartTime} contents={contents} contentsNeedToBeSaved={contentsNeedToBeSaved} updateContentsNeedToBeSaved={updateContentsNeedToBeSaved} questionsToSave={questionsToSave} updateQuestionsToSave={updateQuestionsToSave} status={status} attempt={(attempt !== null && attempt !== undefined) ? attempt : 0} allowedAttempts={allowedAttempts ? allowedAttempts : 0} testsMap={testsMap} uuid={uuid} results={results} language={language}/>
                        </div>                    
                    )
                }
                <Explanation isDarkTheme={isDarkTheme} pollMode={pollMode} grading={grading} useExplanation={useExplanation} explanation={explanation}/>                
            </div>
        </>
    );
}

export default LambdaResponseView;